import React, { useCallback, useRef, useEffect, useState } from 'react';
import {
  ReactFlow,
  Background,
  Controls,
  MiniMap,
  useNodesState,
  useEdgesState,
  Connection,
  NodeChange,
  EdgeChange,
  applyNodeChanges,
  applyEdgeChanges,
  ReactFlowProvider,
  useReactFlow,
  BackgroundVariant,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';

import { useStore, toReactFlowNodes, toReactFlowEdges } from '../store';
import { TaskNode } from './nodes/TaskNode';
import { NoteNode } from './nodes/NoteNode';
import { MilestoneNode } from './nodes/MilestoneNode';
import { TextNode } from './nodes/TextNode';
import { RectangleNode } from './nodes/RectangleNode';
import { CircleNode } from './nodes/CircleNode';
import { DiamondNode } from './nodes/DiamondNode';
import { SearchModal } from './SearchModal';
import { SelectionToolbar } from './SelectionToolbar';

// Register custom node types
const nodeTypes = {
  task: TaskNode,
  note: NoteNode,
  milestone: MilestoneNode,
  text: TextNode,
  rectangle: RectangleNode,
  circle: CircleNode,
  diamond: DiamondNode,
};

function FlowCanvasInner() {
  const reactFlowWrapper = useRef<HTMLDivElement>(null);
  const { fitView, screenToFlowPosition, setCenter } = useReactFlow();
  const [isSearchModalOpen, setIsSearchModalOpen] = useState(false);

  // Get state from Zustand store
  const storeNodes = useStore((state) => state.nodes);
  const storeConnections = useStore((state) => state.connections);
  const selectedNodeId = useStore((state) => state.selectedNodeId);
  const focusNodeId = useStore((state) => state.focusNodeId);
  const addNode = useStore((state) => state.addNode);
  const updateNode = useStore((state) => state.updateNode);
  const deleteNode = useStore((state) => state.deleteNode);
  const selectNode = useStore((state) => state.selectNode);
  const addConnection = useStore((state) => state.addConnection);
  const deleteConnection = useStore((state) => state.deleteConnection);
  const selectEdge = useStore((state) => state.selectEdge);
  const selectedEdgeId = useStore((state) => state.selectedEdgeId);
  const undo = useStore((state) => state.undo);
  const redo = useStore((state) => state.redo);
  const clearFocusNode = useStore((state) => state.clearFocusNode);

  // Convert store state to React Flow format
  const [nodes, setNodes] = useNodesState(toReactFlowNodes(storeNodes));
  const [edges, setEdges] = useEdgesState(toReactFlowEdges(storeConnections));

  // Sync store changes to React Flow state
  useEffect(() => {
    const newNodes = toReactFlowNodes(storeNodes);
    // Don't override selection state - let React Flow manage multi-select
    setNodes((currentNodes) => {
      // Preserve current selection state when syncing from store
      return newNodes.map((newNode) => {
        const currentNode = currentNodes.find(n => n.id === newNode.id);
        return {
          ...newNode,
          selected: currentNode?.selected || false,
        };
      });
    });
  }, [storeNodes, setNodes]);

  useEffect(() => {
    const newEdges = toReactFlowEdges(storeConnections);
    // Update selection state for edges
    const edgesWithSelection = newEdges.map((edge) => ({
      ...edge,
      selected: edge.id === selectedEdgeId,
    }));
    setEdges(edgesWithSelection);
  }, [storeConnections, selectedEdgeId, setEdges]);

  // Handle focus node (from search or panToElement)
  useEffect(() => {
    if (focusNodeId) {
      const node = storeNodes[focusNodeId];
      if (node) {
        // Calculate the center of the node
        // Default node dimensions are approximately 280px wide x 100px tall
        const nodeCenterX = node.x + 140; // half of 280
        const nodeCenterY = node.y + 50;  // half of 100

        // Use setCenter to focus on the node with animation
        // Zoom to 1.2 for better visibility of the searched node
        setCenter(nodeCenterX, nodeCenterY, { zoom: 1.2, duration: 800 });

        // Clear the focus flag after animation completes
        setTimeout(() => clearFocusNode(), 900);
      }
    }
  }, [focusNodeId, storeNodes, setCenter, clearFocusNode]);

  // Handle node changes (position, selection)
  const onNodesChange = useCallback(
    (changes: NodeChange[]) => {
      // Apply changes to local React Flow state
      setNodes((nds) => applyNodeChanges(changes, nds));

      // Sync changes back to store
      changes.forEach((change) => {
        if (change.type === 'position' && change.position && change.id) {
          updateNode(
            change.id,
            { x: change.position.x, y: change.position.y },
            { skipHistory: change.dragging }
          );
        }
        // Sync selection changes to store for the SelectionToolbar
        if (change.type === 'select' && change.id) {
          if (change.selected) {
            selectNode(change.id);
          }
        }
        if (change.type === 'remove' && change.id) {
          deleteNode(change.id);
        }
      });
    },
    [setNodes, updateNode, deleteNode, selectNode]
  );

  // Handle edge changes
  const onEdgesChange = useCallback(
    (changes: EdgeChange[]) => {
      // Filter out removal changes for hierarchy-based connections
      // since connections should only be removed when nodes are deleted
      const filteredChanges = changes.filter((change) => {
        if (change.type === 'remove' && change.id) {
          // Find the connection to check if it's a hierarchy connection
          const connection = storeConnections.find((c) => c.id === change.id);
          if (connection) {
            const targetNode = storeNodes[connection.toId];
            const sourceNode = storeNodes[connection.fromId];

            // Only allow deletion if this is not a parent-child hierarchy connection
            // (i.e., the target's parentId doesn't match the source, or source's children doesn't include target)
            const isHierarchyConnection =
              targetNode?.parentId === connection.fromId ||
              sourceNode?.children?.includes(connection.toId);

            if (isHierarchyConnection) {
              console.log('Cannot delete hierarchy connection - delete the child node instead');
              return false; // Filter out this change
            }
          }
        }
        return true; // Keep other changes
      });

      setEdges((eds) => applyEdgeChanges(filteredChanges, eds));

      filteredChanges.forEach((change) => {
        if (change.type === 'remove' && change.id) {
          deleteConnection(change.id);
        }
      });
    },
    [setEdges, deleteConnection, storeConnections, storeNodes]
  );

  // Handle new connections - DISABLED for automatic hierarchy
  // Manual connections are now disabled since connections are created automatically via hierarchy
  // This is kept for backward compatibility with existing connections
  const onConnect = useCallback(
    (connection: Connection) => {
      // Disabled: Manual connections are no longer allowed
      // Connections are now created automatically when adding children/siblings
      console.log('Manual connections disabled - use + buttons to create hierarchy');
      return;

      // Legacy code (commented out):
      // if (connection.source && connection.target) {
      //   addConnection(connection.source, connection.target);
      // }
    },
    []
  );

  // Note: Double-click is now only for editing text in nodes
  // Removed onDoubleClick handler that created nodes on canvas double-click
  // to avoid interfering with text editing in nodes

  // Handle pane click to deselect
  const onPaneClick = useCallback(() => {
    selectNode(null);
    selectEdge(null);
  }, [selectNode, selectEdge]);

  // Handle edge click to select
  const onEdgeClick = useCallback(
    (_event: React.MouseEvent, edge: { id: string }) => {
      selectEdge(edge.id);
    },
    [selectEdge]
  );

  // Get search functions from store
  const searchNodes = useStore((state) => state.searchNodes);
  const nextSearchResult = useStore((state) => state.nextSearchResult);
  const previousSearchResult = useStore((state) => state.previousSearchResult);
  const searchResults = useStore((state) => state.searchResults);
  const currentSearchIndex = useStore((state) => state.currentSearchIndex);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Don't trigger shortcuts when editing text
      const target = e.target as HTMLElement;
      if (target.isContentEditable || target.tagName === 'INPUT' || target.tagName === 'TEXTAREA') {
        return;
      }

      // Delete selected nodes (supports multi-select)
      if (e.key === 'Delete' || e.key === 'Backspace') {
        if (target === document.body || target.closest('.react-flow')) {
          const selectedNodes = nodes.filter(node => node.selected);
          if (selectedNodes.length > 0) {
            e.preventDefault();
            selectedNodes.forEach(node => deleteNode(node.id));
          }
        }
      }

      // Undo/Redo
      if ((e.metaKey || e.ctrlKey) && e.key === 'z' && !e.shiftKey) {
        e.preventDefault();
        undo();
      }
      if ((e.metaKey || e.ctrlKey) && (e.key === 'Z' || (e.key === 'z' && e.shiftKey))) {
        e.preventDefault();
        redo();
      }

      // Search
      if ((e.metaKey || e.ctrlKey) && e.key === 'f') {
        e.preventDefault();
        setIsSearchModalOpen(true);
      }

      // Navigate search results with F3 / Shift+F3 or Cmd+G / Cmd+Shift+G
      if (e.key === 'F3' || ((e.metaKey || e.ctrlKey) && e.key === 'g')) {
        if (searchResults.length > 0) {
          e.preventDefault();
          if (e.shiftKey) {
            previousSearchResult();
          } else {
            nextSearchResult();
          }
        }
      }

      // Deselect with Escape
      if (e.key === 'Escape') {
        selectNode(null);
        selectEdge(null);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [nodes, deleteNode, undo, redo, searchNodes, nextSearchResult, previousSearchResult, searchResults, setIsSearchModalOpen, selectNode, selectEdge]);

  return (
    <div ref={reactFlowWrapper} className="flow-canvas">
      <SearchModal
        isOpen={isSearchModalOpen}
        onClose={() => setIsSearchModalOpen(false)}
      />
      <ReactFlow
        nodes={nodes}
        edges={edges}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onConnect={onConnect}
        onPaneClick={onPaneClick}
        onEdgeClick={onEdgeClick}
        nodeTypes={nodeTypes}
        fitView={false}
        snapToGrid={true}
        snapGrid={[20, 20]}
        deleteKeyCode={null}
        selectionKeyCode={'Shift'}
        multiSelectionKeyCode={['Meta', 'Control']}
        selectionOnDrag={true}
        panOnScroll={true}
        zoomOnScroll={true}
        panOnDrag={[1, 2]}
        selectNodesOnDrag={false}
        nodeDragThreshold={5}
        // Disable manual connections - connections are now automatic via hierarchy
        connectionMode="loose"
        connectOnClick={false}
        connectionLineStyle={{ stroke: 'var(--color-connection-default)', strokeWidth: 2 }}
        defaultEdgeOptions={{
          type: 'smoothstep',
          style: { stroke: 'var(--color-connection-default)', strokeWidth: 2 },
        }}
      >
        <Background
          variant={BackgroundVariant.Dots}
          gap={20}
          size={1}
          color="var(--color-border-secondary)"
        />
        <Controls showInteractive={false} />
        <MiniMap
          nodeColor={(node) => {
            switch (node.type) {
              case 'task':
                return 'var(--color-node-task)';
              case 'note':
                return 'var(--color-node-note)';
              case 'milestone':
                return 'var(--color-node-milestone)';
              case 'text':
                return 'var(--color-text-secondary)';
              case 'rectangle':
              case 'circle':
              case 'diamond':
                return 'var(--color-text-primary)';
              default:
                return 'var(--color-accent-primary)';
            }
          }}
          maskColor="rgba(0, 0, 0, 0.1)"
        />
      </ReactFlow>

      {/* Selection Toolbar */}
      <SelectionToolbar />

      {/* Search Results Indicator */}
      {searchResults.length > 0 && (
        <div className="search-results-indicator">
          Result {currentSearchIndex + 1} of {searchResults.length}
          <div className="search-hint">
            Press F3 or Cmd+G for next, Shift+F3 or Cmd+Shift+G for previous
          </div>
        </div>
      )}
    </div>
  );
}

export function FlowCanvas() {
  return (
    <ReactFlowProvider>
      <FlowCanvasInner />
    </ReactFlowProvider>
  );
}
